/**
 * PlateCalculator — Shows plates needed per side for a target barbell weight.
 * Visual plate diagram with configurable bar weight.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { X } from 'lucide-react-native';

interface PlateCalculatorProps {
  visible: boolean;
  onClose: () => void;
  initialWeight?: number;
  unit?: 'lb' | 'kg';
}

const PLATES_LB = [45, 35, 25, 10, 5, 2.5];
const PLATES_KG = [25, 20, 15, 10, 5, 2.5, 1.25];
const DEFAULT_BAR_LB = 45;
const DEFAULT_BAR_KG = 20;

const PLATE_COLORS: Record<number, string> = {
  // LB plates
  45: '#ef4444',   // Red
  35: '#3b82f6',   // Blue
  25: '#22c55e',   // Green
  10: '#f59e0b',   // Yellow/Amber
  5: '#8b5cf6',    // Purple
  2.5: '#6b7280',  // Grey
  // KG plates
  20: '#3b82f6',
  15: '#f59e0b',
  1.25: '#a3a3a3',
};

function calculatePlates(targetWeight: number, barWeight: number, availablePlates: number[]): number[] {
  let remaining = (targetWeight - barWeight) / 2;
  if (remaining <= 0) return [];
  const result: number[] = [];
  for (const plate of availablePlates) {
    while (remaining >= plate - 0.001) {
      result.push(plate);
      remaining -= plate;
    }
  }
  return result;
}

export default function PlateCalculator({ visible, onClose, initialWeight, unit = 'lb' }: PlateCalculatorProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const plates = unit === 'kg' ? PLATES_KG : PLATES_LB;
  const defaultBar = unit === 'kg' ? DEFAULT_BAR_KG : DEFAULT_BAR_LB;

  const [targetWeight, setTargetWeight] = useState(initialWeight?.toString() || '');
  // Update when initialWeight changes (e.g. re-opened with a new prefill)
  useEffect(() => {
    if (visible && initialWeight !== undefined) {
      setTargetWeight(initialWeight.toString());
    }
  }, [visible, initialWeight]);
  const [barWeight, setBarWeight] = useState(defaultBar.toString());

  const target = parseFloat(targetWeight) || 0;
  const bar = parseFloat(barWeight) || defaultBar;

  const platesPerSide = useMemo(() => calculatePlates(target, bar, plates), [target, bar, plates]);
  const totalLoaded = platesPerSide.reduce((s, p) => s + p, 0) * 2 + bar;
  const isExact = Math.abs(totalLoaded - target) < 0.01;
  const remainder = target - totalLoaded;

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={[styles.content, { backgroundColor: c.surface, borderColor: c.border }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text }]}>Plate Calculator</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <X size={20} color={c.textTertiary} />
            </TouchableOpacity>
          </View>

          {/* Inputs */}
          <View style={styles.inputRow}>
            <View style={styles.inputGroup}>
              <Text style={[styles.label, { color: c.textSecondary }]}>TARGET ({unit})</Text>
              <TextInput
                style={[styles.input, { color: c.text, borderColor: c.border, backgroundColor: c.surfaceVariant }]}
                value={targetWeight}
                onChangeText={setTargetWeight}
                keyboardType="numeric"
                placeholder="225"
                placeholderTextColor={c.textTertiary}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={[styles.label, { color: c.textSecondary }]}>BAR ({unit})</Text>
              <TextInput
                style={[styles.input, { color: c.text, borderColor: c.border, backgroundColor: c.surfaceVariant }]}
                value={barWeight}
                onChangeText={setBarWeight}
                keyboardType="numeric"
                placeholder={defaultBar.toString()}
                placeholderTextColor={c.textTertiary}
              />
            </View>
          </View>

          {/* Visual Plate Diagram */}
          {target > 0 && (
            <View style={styles.diagramContainer}>
              <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>EACH SIDE</Text>

              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.diagram}>
                  {/* Bar */}
                  <View style={[styles.barEnd, { backgroundColor: c.textTertiary }]} />

                  {/* Plates (left/loading side) */}
                  {platesPerSide.map((plate, i) => {
                    const plateColor = PLATE_COLORS[plate] || c.accent;
                    const height = 20 + (plate / (unit === 'kg' ? 25 : 45)) * 50;
                    return (
                      <View
                        key={i}
                        style={[styles.plateBlock, {
                          backgroundColor: plateColor,
                          height,
                          minWidth: 18,
                        }]}
                      >
                        <Text style={styles.plateLabel}>{plate}</Text>
                      </View>
                    );
                  })}

                  {/* Collar */}
                  <View style={[styles.collar, { backgroundColor: c.textTertiary }]} />

                  {/* Bar body */}
                  <View style={[styles.barBody, { backgroundColor: c.textTertiary + '60' }]} />
                </View>
              </ScrollView>

              {/* Plate list */}
              <View style={styles.plateList}>
                {platesPerSide.length === 0 ? (
                  <Text style={[styles.noPlates, { color: c.textTertiary }]}>
                    {target <= bar ? 'Bar only — no plates needed' : 'Enter a weight above the bar'}
                  </Text>
                ) : (
                  platesPerSide.map((plate, i) => (
                    <View
                      key={i}
                      style={[styles.plateChip, {
                        backgroundColor: (PLATE_COLORS[plate] || c.accent) + '20',
                        borderColor: (PLATE_COLORS[plate] || c.accent) + '40',
                      }]}
                    >
                      <View style={[styles.plateColorDot, { backgroundColor: PLATE_COLORS[plate] || c.accent }]} />
                      <Text style={[styles.plateChipText, { color: c.text }]}>{plate} {unit}</Text>
                    </View>
                  ))
                )}
              </View>

              {/* Total & accuracy */}
              <View style={[styles.totalRow, { borderColor: c.border }]}>
                <Text style={[styles.totalLabel, { color: c.textSecondary }]}>Loaded weight:</Text>
                <Text style={[styles.totalValue, { color: isExact ? c.success : c.warning }]}>
                  {totalLoaded} {unit}
                  {!isExact && remainder > 0 && ` (${remainder.toFixed(1)} ${unit} short)`}
                </Text>
              </View>
            </View>
          )}

          {/* Quick weight buttons */}
          <View style={styles.quickWeights}>
            {(unit === 'kg'
              ? [60, 80, 100, 120, 140, 160]
              : [135, 185, 225, 275, 315, 405]
            ).map(w => (
              <TouchableOpacity
                key={w}
                style={[styles.quickBtn, {
                  backgroundColor: target === w ? c.accent + '25' : c.surfaceVariant,
                  borderColor: target === w ? c.accent : c.border,
                }]}
                onPress={() => setTargetWeight(w.toString())}
              >
                <Text style={[styles.quickBtnText, {
                  color: target === w ? c.accent : c.textSecondary,
                }]}>
                  {w}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'flex-end',
  },
  content: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1,
    borderBottomWidth: 0,
    padding: 20,
    gap: 16,
    maxHeight: '85%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 18,
    fontWeight: '800',
  },
  closeBtn: {
    padding: 4,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 12,
  },
  inputGroup: {
    flex: 1,
  },
  label: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 8,
  },
  diagramContainer: {
    gap: 12,
  },
  diagram: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  barEnd: {
    width: 4,
    height: 14,
    borderRadius: 2,
  },
  plateBlock: {
    width: 18,
    borderRadius: 3,
    marginLeft: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  plateLabel: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '800',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  collar: {
    width: 6,
    height: 20,
    borderRadius: 2,
    marginLeft: 2,
  },
  barBody: {
    width: 60,
    height: 8,
    borderRadius: 4,
    marginLeft: 0,
  },
  plateList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  plateChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 2,
    borderWidth: 1,
  },
  plateColorDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  plateChipText: {
    fontSize: 13,
    fontWeight: '600',
  },
  noPlates: {
    fontSize: 13,
    fontStyle: 'italic',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  totalLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  totalValue: {
    fontSize: 14,
    fontWeight: '700',
  },
  quickWeights: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    justifyContent: 'center',
  },
  quickBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 2,
    borderWidth: 1,
  },
  quickBtnText: {
    fontSize: 13,
    fontWeight: '700',
  },
});
